import React, { useMemo, useState } from 'react';
import {
  DEFAULT_UNIT_ECONOMICS_INPUTS,
  UNIT_ECONOMICS_FORMULA_STEPS,
  calculateUnitEconomicsMetrics,
  formatMoney,
  formatPercent,
  getInsightState,
} from './unitEconomicsLogic';
import UnitEconomicsNumberField from './UnitEconomicsNumberField';
import { formatTrustedMessage } from '../../utils/htmlSecurity';
import './UnitEconomics.css';

const CalcRow = ({ label, value, tone = 'default' }) => (
  <div className="ue-calc-row">
    <span className="ue-calc-label">{label}</span>
    <span
      className={`ue-calc-val${
        tone === 'neg' ? ' ue-calc-val--neg' : tone === 'neutral' ? ' ue-calc-val--neutral' : ''
      }`}
    >
      {value}
    </span>
  </div>
);

const UnitEconomics = () => {
  const [inputs, setInputs] = useState(DEFAULT_UNIT_ECONOMICS_INPUTS);
  const locale = 'ru-RU';

  const metrics = useMemo(() => calculateUnitEconomicsMetrics(inputs), [inputs]);
  const insight = useMemo(() => getInsightState(metrics), [metrics]);

  const insightMessage = useMemo(
    () => formatTrustedMessage(insight.message, insight.vars || {}),
    [insight],
  );

  const handleChange = (field, value) => {
    setInputs((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const heroProfitColor = metrics.profitTotal < 0 ? '#f87c6b' : undefined;

  return (
    <div className="ue-calculator">
      <div className="ue-legend">
        <div className="ue-legend-item">
          <span className="ue-legend-dot ue-legend-dot--input" />
          {'Вводите вы'}
        </div>
        <div className="ue-legend-item">
          <span className="ue-legend-dot ue-legend-dot--calc" />
          {'Считается автоматически'}
        </div>
      </div>

      <div className="ue-layout">
        <section className="ue-panel">
          <h2 className="ue-panel-title">{'Выручка и продажи'}</h2>
          <UnitEconomicsNumberField
            label={'Выручка за период'}
            unit={'₸'}
            value={inputs.revenue}
            min={0}
            onChange={(value) => handleChange('revenue', value)}
          />
          <UnitEconomicsNumberField
            label={'Количество продаж'}
            unit={'шт'}
            value={inputs.sales}
            min={1}
            onChange={(value) => handleChange('sales', value)}
          />
          <UnitEconomicsNumberField
            label={'Период'}
            unit={'дн'}
            value={inputs.period}
            min={1}
            onChange={(value) => handleChange('period', value)}
          />
          <div className="ue-sep" />
          <CalcRow label={'Средний чек'} value={formatMoney(metrics.avgCheck, locale)} tone="neutral" />
          <CalcRow
            label={'Продаж в день'}
            value={`${metrics.perDay.toFixed(2)} ${'шт/день'}`}
            tone="neutral"
          />
        </section>

        <section className="ue-panel">
          <h2 className="ue-panel-title">{'Расходы за период'}</h2>
          <UnitEconomicsNumberField
            label={'Себестоимость'}
            hint={'материалы, товар'}
            unit={'₸'}
            value={inputs.cogs}
            min={0}
            onChange={(value) => handleChange('cogs', value)}
          />
          <UnitEconomicsNumberField
            label={'Маркетинговые расходы'}
            hint={'реклама'}
            unit={'₸'}
            value={inputs.marketing}
            min={0}
            onChange={(value) => handleChange('marketing', value)}
          />
          <UnitEconomicsNumberField
            label={'Налоги и прочие расходы'}
            unit={'₸'}
            value={inputs.tax}
            min={0}
            onChange={(value) => handleChange('tax', value)}
          />
          <div className="ue-sep" />
          <CalcRow label={'Расходы на 1 продажу'} value={formatMoney(metrics.costPerSale, locale)} tone="neutral" />
          <CalcRow label={'Суммарные расходы'} value={formatMoney(metrics.totalCost, locale)} tone="neutral" />
        </section>
      </div>

      <div className="ue-layout">
        <article className="ue-result-hero">
          <div className="ue-result-hero-label">{'Прибыль суммарная за период'}</div>
          <div
            className="ue-result-hero-value"
            style={heroProfitColor ? { color: heroProfitColor } : undefined}
          >
            {formatMoney(metrics.profitTotal, locale)}
          </div>
          <div className="ue-result-hero-sub">
            {`${metrics.marginFact} маржа · ${metrics.profitPerSale} на продажу`}
          </div>
        </article>

        <section className="ue-panel ue-panel--profit-side">
          <h2 className="ue-panel-title">{'Прибыль на 1 продажу'}</h2>
          <CalcRow
            label={'Прибыль на 1 продажу'}
            value={formatMoney(metrics.profitPerSale, locale)}
            tone={metrics.profitPerSale < 0 ? 'neg' : 'default'}
          />
          <CalcRow label={'Маржинальность факт'} value={formatPercent(metrics.marginFact)} />
          <UnitEconomicsNumberField
            label={'Желаемая маржинальность'}
            hint={'цель'}
            unit={'%'}
            value={inputs.marginWant}
            min={1}
            max={99}
            onChange={(value) => handleChange('marginWant', value)}
          />
        </section>
      </div>

      <div className="ue-layout ue-layout--spaced">
        <section className="ue-panel">
          <h2 className="ue-panel-title">{'Воронка и стоимость лида'}</h2>
          <UnitEconomicsNumberField
            label={'CR1 — конверсия клик → лид'}
            unit={'%'}
            value={inputs.cr1}
            min={0.1}
            max={100}
            step={0.1}
            onChange={(value) => handleChange('cr1', value)}
          />
          <UnitEconomicsNumberField
            label={'CR2 — конверсия лид → продажа'}
            unit={'%'}
            value={inputs.cr2}
            min={0.1}
            max={100}
            step={0.1}
            onChange={(value) => handleChange('cr2', value)}
          />
          <div className="ue-sep" />
          <CalcRow label={'CPS — допустимые расходы на продажу'} value={formatMoney(metrics.cps, locale)} />
          <CalcRow label={'CPL — стоимость лида'} value={formatMoney(metrics.cpl, locale)} />
          <CalcRow label={'CPC — стоимость клика'} value={formatMoney(metrics.cpc, locale)} />
        </section>

        <section className="ue-panel">
          <h2 className="ue-panel-title">{'План на следующий месяц'}</h2>
          <UnitEconomicsNumberField
            label={'Желаемый рост продаж'}
            unit={'%'}
            value={inputs.growth}
            min={-50}
            max={500}
            onChange={(value) => handleChange('growth', value)}
          />
          <div className="ue-sep" />
          <CalcRow
            label={'План продаж (шт)'}
            value={`${metrics.planSales} ${'шт'}`}
            tone="neutral"
          />
          <CalcRow label={'Бюджет на рекламу'} value={formatMoney(metrics.planBudget, locale)} />
          <CalcRow label={'Ожидаемая выручка'} value={formatMoney(metrics.planRevenue, locale)} />
          <CalcRow
            label={'Ожидаемая прибыль'}
            value={formatMoney(metrics.planProfit, locale)}
            tone={metrics.planProfit < 0 ? 'neg' : 'default'}
          />
        </section>
      </div>

      <div
        className={`ue-insight ue-insight--${insight.variant}`}
        dangerouslySetInnerHTML={{ __html: insightMessage }}
      />

      <section className="ue-formula-panel">
        <h2 className="ue-panel-title">{'Как считается — шаг за шагом'}</h2>
        {UNIT_ECONOMICS_FORMULA_STEPS.map((step) => (
          <article key={step.marker} className="ue-formula-step">
            <div className="ue-formula-step-num">{step.marker}</div>
            <div>
              <div className="ue-formula-step-title">{step.title}</div>
              <div className="ue-formula-step-formula">{step.formula}</div>
              <div
                className="ue-formula-step-example"
                dangerouslySetInnerHTML={{
                  __html: step.example,
                }}
              />
            </div>
          </article>
        ))}
      </section>

      <p className="ue-footer-note">{'Юнит-экономика на минималках · считайте до запуска рекламы'}</p>
    </div>
  );
};

export default UnitEconomics;
